export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public field?: string,
    public payload?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function csrfToken(): string {
  const m = document.cookie.match(/(?:^|;\s*)da_csrf=([^;]+)/)
  return m ? decodeURIComponent(m[1]) : ''
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {}
  let payload: BodyInit | undefined

  if (body instanceof FormData) {
    payload = body
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify(body)
  }

  if (method !== 'GET') headers['x-csrf-token'] = csrfToken()

  const res = await fetch(`/api${path}`, {
    method,
    headers,
    body: payload,
    credentials: 'same-origin',
  })

  if (res.status === 204) return undefined as T

  const contentType = res.headers.get('content-type') ?? ''
  if (!contentType.includes('application/json')) {
    if (!res.ok) throw new ApiError(await res.text(), res.status)
    return (await res.blob()) as T
  }

  const json = await res.json()
  if (!res.ok) {
    const err = json as { error?: string; field?: string; message?: string }
    throw new ApiError(err.message || err.error || 'Request failed', res.status, err.field, json)
  }
  return json as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
}

// ------------------------------------------------------------------- helpers

/** Paise -> "₹1,23,456.00". Indian digit grouping for INR, Western otherwise. */
export function money(paise: number | null | undefined, currency = 'INR'): string {
  if (paise === null || paise === undefined) return '—'
  const symbols: Record<string, string> = { INR: '₹', USD: '$', EUR: '€', GBP: '£' }
  const sym = symbols[currency] ?? currency + ' '
  const neg = paise < 0
  const abs = Math.abs(paise)
  const int = Math.floor(abs / 100).toString()
  const dec = (abs % 100).toString().padStart(2, '0')
  const grouped =
    currency === 'INR' && int.length > 3
      ? int.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + int.slice(-3)
      : int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${neg ? '-' : ''}${sym}${grouped}.${dec}`
}

export function toPaise(v: string | number): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[, ]/g, ''))
  return isFinite(n) ? Math.round(n * 100) : 0
}

export function fromPaise(paise: number): string {
  return (paise / 100).toFixed(2)
}

export function fmtDate(s: string | null | undefined): string {
  if (!s) return '—'
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const [y, m, d] = s.slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return s
  return `${String(d).padStart(2, '0')} ${months[m - 1]} ${y}`
}

export function today(): string {
  // Render today's date in IST regardless of where the browser is.
  const now = new Date(Date.now() + 330 * 60_000)
  return now.toISOString().slice(0, 10)
}

export function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

export function daysBetween(a: string, b: string): number {
  return Math.round(
    (new Date(b + 'T00:00:00Z').getTime() - new Date(a + 'T00:00:00Z').getTime()) / 86_400_000,
  )
}

export const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  issued: 'Issued',
  sent: 'Sent',
  viewed: 'Viewed',
  partially_paid: 'Partly paid',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
  written_off: 'Written off',
}

export const DOC_LABELS: Record<string, string> = {
  tax_invoice: 'Tax invoice',
  proforma: 'Proforma',
  estimate: 'Estimate',
  credit_note: 'Credit note',
  debit_note: 'Debit note',
  bill_of_supply: 'Bill of supply',
}

export const CLIENT_TYPE_LABELS: Record<string, string> = {
  domestic_b2b: 'Domestic B2B (registered)',
  domestic_b2c: 'Domestic B2C (unregistered)',
  export: 'Export of services',
  sez: 'SEZ unit / developer',
}
